import { useEffect, useState, useCallback } from 'react'
import DOMPurify from 'dompurify'
import { useNavigate, useLocation, useParams } from 'react-router'
import { getAllPostsAdmin, renderMarkdown, type BlogPost } from '../lib/blog'

/* ------------------------------------------------------------------ */
/*  Password Gate                                                       */
/* ------------------------------------------------------------------ */

function PasswordGate({ onUnlock }: { onUnlock: () => void }) {
  const [pw, setPw] = useState('')
  const [error, setError] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (pw === 'punchthemonkey') {
      sessionStorage.setItem('admin_unlocked', 'true')
      onUnlock()
    } else {
      setError(true)
      setPw('')
    }
  }

  return (
    <div className="min-h-screen bg-void flex items-center justify-center px-5">
      <div className="w-full max-w-sm bg-obsidian border border-carbon rounded p-10">
        <p className="text-subhead uppercase tracking-[0.08em] text-steel text-center mb-2">
          Primate Intelligence
        </p>
        <h1 className="text-h3 text-pure text-center mb-8">Admin Access</h1>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <input
            type="password"
            value={pw}
            onChange={e => { setPw(e.target.value); setError(false) }}
            placeholder="Password"
            autoFocus
            className="rounded-md border border-carbon bg-carbon/60 px-4 py-3 text-body text-pure placeholder:text-steel focus:outline-none focus:ring-2 focus:ring-signal focus:border-transparent"
          />
          {error && (
            <p className="text-body-sm" style={{ color: '#EF4444' }}>Incorrect password.</p>
          )}
          <button
            type="submit"
            className="inline-flex items-center justify-center rounded-md bg-signal px-6 py-3 text-body font-semibold text-white shadow-signal transition-all duration-default hover:bg-signal-hover"
          >
            Enter
          </button>
        </form>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Sidebar                                                             */
/* ------------------------------------------------------------------ */

function Sidebar({ activeView }: { activeView: string }) {
  const navigate = useNavigate()

  const handleLogout = () => {
    sessionStorage.removeItem('admin_unlocked')
    window.location.reload()
  }

  return (
    <div className="w-[220px] flex-shrink-0 bg-obsidian border-r border-carbon flex flex-col min-h-screen">
      <div className="p-6 border-b border-carbon">
        <p className="text-subhead font-bold text-pure tracking-widest">PRIMATE AI</p>
        <span className="mt-1 inline-block text-caption-1 font-bold px-2 py-0.5 rounded-full bg-signal/10 text-signal">
          Admin
        </span>
      </div>
      <nav className="flex-1 p-4 flex flex-col gap-1">
        <button
          onClick={() => navigate('/admin')}
          className={`w-full text-left px-3 py-2 rounded-md text-body-sm transition-colors duration-fast ${
            activeView === 'list' ? 'bg-carbon text-pure' : 'text-steel hover:text-pure hover:bg-carbon/50'
          }`}
        >
          Posts
        </button>
        <button
          onClick={() => navigate('/admin/new')}
          className={`w-full text-left px-3 py-2 rounded-md text-body-sm transition-colors duration-fast ${
            activeView === 'new' ? 'bg-carbon text-pure' : 'text-steel hover:text-pure hover:bg-carbon/50'
          }`}
        >
          New Post
        </button>
        <button
          onClick={() => navigate('/admin?view=drafts')}
          className={`w-full text-left px-3 py-2 rounded-md text-body-sm transition-colors duration-fast ${
            activeView === 'drafts' ? 'bg-carbon text-pure' : 'text-steel hover:text-pure hover:bg-carbon/50'
          }`}
        >
          Drafts
        </button>
      </nav>
      <div className="p-4 border-t border-carbon">
        <p className="text-footnote text-steel mb-2 truncate">admin@primateintelligence.ai</p>
        <button
          onClick={handleLogout}
          className="text-footnote text-steel hover:text-pure transition-colors duration-fast"
        >
          Log out
        </button>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Posts List View                                                     */
/* ------------------------------------------------------------------ */

function PostsList() {
  const navigate = useNavigate()
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [hidden, setHidden] = useState<Set<string>>(new Set())
  const [toast, setToast] = useState<string | null>(null)

  useEffect(() => {
    getAllPostsAdmin().then(setPosts)
  }, [])

  const handleDelete = (post: BlogPost) => {
    if (!confirm(`Remove "${post.title}" from this view?`)) return
    setHidden(prev => new Set([...prev, post.slug]))
    setToast(`Delete the file src/content/posts/${post.slug}.md to permanently remove.`)
    setTimeout(() => setToast(null), 6000)
  }

  const visible = posts.filter(p => !hidden.has(p.slug))

  return (
    <div className="flex-1 p-8 overflow-auto">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-h3 text-pure">Posts</h2>
        <button
          onClick={() => navigate('/admin/new')}
          className="inline-flex items-center gap-2 rounded-md bg-signal px-4 py-2 text-body-sm font-semibold text-white shadow-signal transition-all duration-default hover:bg-signal-hover"
        >
          New Post
        </button>
      </div>

      {toast && (
        <div className="mb-6 rounded border border-carbon bg-obsidian px-4 py-3 text-body-sm text-steel">
          {toast}
        </div>
      )}

      <div className="border border-carbon rounded overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-carbon bg-carbon/30">
              <th className="text-left px-5 py-3 text-footnote font-semibold text-steel uppercase tracking-wider">Title</th>
              <th className="text-left px-5 py-3 text-footnote font-semibold text-steel uppercase tracking-wider">Author</th>
              <th className="text-left px-5 py-3 text-footnote font-semibold text-steel uppercase tracking-wider">Status</th>
              <th className="text-left px-5 py-3 text-footnote font-semibold text-steel uppercase tracking-wider">Date</th>
              <th className="text-left px-5 py-3 text-footnote font-semibold text-steel uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((post, i) => (
              <tr key={post.slug} className={`border-b border-carbon last:border-0 ${i % 2 === 0 ? '' : 'bg-carbon/10'}`}>
                <td className="px-5 py-4 text-body-sm text-pure font-medium max-w-[280px] truncate">{post.title}</td>
                <td className="px-5 py-4 text-body-sm text-steel">{post.author}</td>
                <td className="px-5 py-4">
                  {post.status === 'published' ? (
                    <span className="text-caption-1 font-bold px-2 py-0.5 rounded-full bg-success/10 text-success-dark">published</span>
                  ) : (
                    <span className="text-caption-1 font-bold px-2 py-0.5 rounded-full bg-carbon text-steel">draft</span>
                  )}
                </td>
                <td className="px-5 py-4 text-body-sm text-steel">{post.date}</td>
                <td className="px-5 py-4 flex items-center gap-3">
                  <button
                    onClick={() => navigate(`/admin/edit/${post.slug}`)}
                    className="text-body-sm text-signal hover:text-signal-hover transition-colors duration-fast"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(post)}
                    className="text-body-sm text-steel hover:text-error-dark transition-colors duration-fast"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Post Editor                                                         */
/* ------------------------------------------------------------------ */

const AUTHORS = ['Matt Miesnieks', 'Mehdi Nikkhah']

function PostEditor({ editSlug }: { editSlug?: string }) {
  const [title, setTitle] = useState('')
  const [author, setAuthor] = useState(AUTHORS[0])
  const [status, setStatus] = useState<'published' | 'draft'>('draft')
  const [date, setDate] = useState(new Date().toISOString().split('T')[0])
  const [tags, setTags] = useState('')
  const [excerpt, setExcerpt] = useState('')
  const [content, setContent] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (editSlug) {
      getAllPostsAdmin().then(all => {
        const post = all.find(p => p.slug === editSlug)
        if (post) {
          setTitle(post.title)
          setAuthor(post.author)
          setStatus(post.status)
          setDate(post.date)
          setTags(post.tags.join(', '))
          setExcerpt(post.excerpt)
          setContent(post.content)
        }
      })
    }
  }, [editSlug])

  const slug = editSlug ?? title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

  const authorInitials = author.split(' ').map(part => part[0]).join('').toUpperCase().slice(0, 2)

  const generatedMarkdown = `---
title: "${title}"
slug: "${slug}"
author: "${author}"
authorInitials: "${authorInitials}"
date: "${date}"
readTime: "5 min read"
tags: [${tags.split(',').map(t => `"${t.trim()}"`).join(', ')}]
status: "${status}"
excerpt: "${excerpt}"
---

${content}`

  const insertAtCursor = useCallback((before: string, after = '') => {
    const ta = document.getElementById('post-content') as HTMLTextAreaElement | null
    if (!ta) return
    const start = ta.selectionStart
    const end = ta.selectionEnd
    const selected = content.slice(start, end)
    const newContent = content.slice(0, start) + before + selected + after + content.slice(end)
    setContent(newContent)
    setTimeout(() => {
      ta.focus()
      ta.setSelectionRange(start + before.length, start + before.length + selected.length)
    }, 0)
  }, [content])

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedMarkdown).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-8 py-4 border-b border-carbon">
        <h2 className="text-title-1 text-pure">{editSlug ? 'Edit Post' : 'New Post'}</h2>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 rounded-md bg-signal px-4 py-2 text-body-sm font-semibold text-white shadow-signal transition-all duration-default hover:bg-signal-hover"
        >
          Save / Export
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Form */}
        <div className="w-[60%] flex flex-col gap-5 p-8 overflow-auto border-r border-carbon">
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Post title"
            className="w-full rounded-md border border-carbon bg-carbon/60 px-4 py-3 text-title-1 text-pure placeholder:text-steel focus:outline-none focus:ring-2 focus:ring-signal"
          />

          <div className="flex gap-4 flex-wrap">
            <div className="flex flex-col gap-1">
              <label className="text-footnote text-steel">Author</label>
              <select
                value={author}
                onChange={e => setAuthor(e.target.value)}
                className="rounded-md border border-carbon bg-carbon/60 px-3 py-2 text-body-sm text-pure focus:outline-none focus:ring-2 focus:ring-signal"
              >
                {AUTHORS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-footnote text-steel">Status</label>
              <div className="flex rounded-md border border-carbon overflow-hidden">
                {(['draft', 'published'] as const).map(s => (
                  <button
                    key={s}
                    onClick={() => setStatus(s)}
                    className={`px-4 py-2 text-body-sm font-medium transition-colors duration-fast ${
                      status === s ? 'bg-signal text-white' : 'bg-carbon/60 text-steel hover:text-chalk'
                    }`}
                  >
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-footnote text-steel">Date</label>
              <input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                className="rounded-md border border-carbon bg-carbon/60 px-3 py-2 text-body-sm text-pure focus:outline-none focus:ring-2 focus:ring-signal"
              />
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-footnote text-steel">Tags (comma-separated)</label>
            <input
              type="text"
              value={tags}
              onChange={e => setTags(e.target.value)}
              placeholder="Research, JEPA"
              className="rounded-md border border-carbon bg-carbon/60 px-4 py-3 text-body-sm text-pure placeholder:text-steel focus:outline-none focus:ring-2 focus:ring-signal"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-footnote text-steel">Excerpt</label>
            <textarea
              value={excerpt}
              onChange={e => setExcerpt(e.target.value)}
              rows={3}
              placeholder="One or two sentences describing the post..."
              className="rounded-md border border-carbon bg-carbon/60 px-4 py-3 text-body-sm text-pure placeholder:text-steel focus:outline-none focus:ring-2 focus:ring-signal resize-y"
            />
          </div>

          {/* Markdown toolbar */}
          <div className="flex flex-col gap-1">
            <label className="text-footnote text-steel">Content</label>
            <div className="flex gap-1 mb-1">
              {[
                { label: 'B', action: () => insertAtCursor('**', '**') },
                { label: 'I', action: () => insertAtCursor('*', '*') },
                { label: 'H2', action: () => insertAtCursor('\n## ') },
                { label: 'H3', action: () => insertAtCursor('\n### ') },
                { label: 'Link', action: () => insertAtCursor('[', '](url)') },
                { label: 'Code', action: () => insertAtCursor('`', '`') },
              ].map(btn => (
                <button
                  key={btn.label}
                  type="button"
                  onClick={btn.action}
                  className="px-2.5 py-1 text-footnote font-mono text-steel bg-carbon/60 border border-carbon rounded hover:text-pure transition-colors duration-fast"
                >
                  {btn.label}
                </button>
              ))}
            </div>
            <textarea
              id="post-content"
              value={content}
              onChange={e => setContent(e.target.value)}
              rows={18}
              placeholder="Write your post in markdown..."
              className="rounded-md border border-carbon bg-carbon/60 px-4 py-3 text-body-sm text-pure placeholder:text-steel focus:outline-none focus:ring-2 focus:ring-signal resize-y font-mono"
            />
          </div>
        </div>

        {/* Preview */}
        <div className="w-[40%] p-8 overflow-auto">
          <p className="text-footnote uppercase tracking-wider text-steel mb-4">Preview</p>
          {title && <h1 className="text-h3 text-pure mb-4">{title}</h1>}
          <div
            className="text-body text-steel leading-relaxed"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderMarkdown(content || '_Start writing to see preview…_')) }}
          />
        </div>
      </div>

      {/* Save modal */}
      {showModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-void/80 backdrop-blur-sm px-5">
          <div className="w-full max-w-2xl bg-obsidian border border-carbon rounded p-8">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-title-1 text-pure font-semibold">Your post is ready</h3>
                <p className="text-body-sm text-steel mt-1">To publish it, save this file:</p>
                <code className="text-body-sm text-signal mt-1 block">
                  src/content/posts/{slug}.md
                </code>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-steel hover:text-pure text-title-1 leading-none"
              >
                ×
              </button>
            </div>
            <pre className="bg-carbon/40 border border-carbon rounded p-4 text-footnote text-steel overflow-auto max-h-64 font-mono whitespace-pre-wrap break-words">
              {generatedMarkdown}
            </pre>
            <button
              onClick={copyToClipboard}
              className="mt-4 inline-flex items-center gap-2 rounded-md bg-signal px-4 py-2 text-body-sm font-semibold text-white shadow-signal transition-all duration-default hover:bg-signal-hover"
            >
              {copied ? 'Copied!' : 'Copy to clipboard'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Admin root                                                          */
/* ------------------------------------------------------------------ */

export default function Admin() {
  const location = useLocation()
  const { slug: editSlug } = useParams<{ slug: string }>()
  const [unlocked, setUnlocked] = useState(
    sessionStorage.getItem('admin_unlocked') === 'true'
  )

  if (!unlocked) {
    return <PasswordGate onUnlock={() => setUnlocked(true)} />
  }

  const isNew = location.pathname === '/admin/new'
  const isEdit = location.pathname.startsWith('/admin/edit/')
  const isDrafts = location.search.includes('view=drafts')

  const activeView = isNew ? 'new' : isEdit ? 'edit' : isDrafts ? 'drafts' : 'list'

  return (
    <div className="min-h-screen bg-void text-pure flex">
      <Sidebar activeView={activeView} />
      {(isNew || isEdit) ? (
        <PostEditor editSlug={editSlug} />
      ) : (
        <PostsList />
      )}
    </div>
  )
}
